import { getListRowLocationDisplay } from "./addressUtils";
import { replaceRfiTerminologyForDisplay } from "./apiResponseRfiToVqqDisplay";
import {
  formatScopeWithAdditionalScopeForExport,
  shouldShowAdditionalScopeOnLibraryItem,
} from "./scopeDisplayUtils";
import { getGridArchiveCommentDisplay } from "./gridArchiveCommentDisplay";
import { formatRiskSummaryForExport } from "../components/RFIs/RiskSummaryCell";
import {
  getLibraryRequestRiskSummary,
  withoutRiskSummaryColumn,
} from "./riskSummaryGridUtils";
import {
  formatGridDate,
  sortConfigToApiSortParamsWithFieldMap,
} from "./myRfiGridRowMapper";
import { LIBRARY_REQUEST_STATUS_ORDER } from "./libraryRequestStatusFilterOptions";

/**
 * Toggle server-side sort for `Documents/Requests` vs client-side sort on the current page only.
 * Set to false if the API ignores `sort_by` / `sort_direction` or while debugging ordering.
 */
export const LIBRARY_REQUESTS_USE_SERVER_SORT = true;

/**
 * First-load sort for Library Requests grids (sponsor / vendor / admin).
 * Maps to Documents/Requests `sort_by=requested_on` / `sort_direction=desc`.
 */
export const LIBRARY_REQUESTS_DEFAULT_SORT_CONFIG = {
  requested_on: { direction: "desc" },
};

/**
 * CustomTable column keys → `sort_by` field names for `Documents/Requests`.
 */
export const LIBRARY_REQUESTS_API_ORDER_BY_FIELD = {
  /** Data field for non-RFI rows; column key in CustomTable is `report_type` */
  document_name: "document_name",
  report_type: "report_type",
  provider_name: "provider_name",
  sponsor_name: "sponsor_name",
  location: "location",
  request_status: "request_status",
  published_on: "published",
  requested_on: "requested_on",
  released_on: "released_on",
  cycle_time: "cycle_time",
};

/**
 * @param {Record<string, { direction?: string }>} sortConfig
 * @returns {{ orderBy: string | null, sortDirection: 'asc' | 'desc' | null }}
 */
export function sortConfigToLibraryRequestsApiParams(sortConfig) {
  return sortConfigToApiSortParamsWithFieldMap(
    LIBRARY_REQUESTS_API_ORDER_BY_FIELD,
    sortConfig
  );
}

/**
 * Scope labels from capabilities, comma-separated (same source as Scope column).
 */
export function getLibraryRequestScopeCommaSeparated(item) {
  if (!item?.capabilities?.length) return "";
  const raw = item.capabilities
    .map((c) =>
      String(c?.rfi_name || c?.services || c?.name || "").trim()
    )
    .filter(Boolean)
    .join(", ");
  return typeof raw === "string"
    ? replaceRfiTerminologyForDisplay(raw)
    : raw;
}

/**
 * Document Name column: "{scope} - RFI" or "Assessment - {scope}" from capabilities.
 */
export function getLibraryRequestDocumentName(item) {
  if (!item) return "";
  const scopeStr = getLibraryRequestScopeCommaSeparated(item);
  const isRfi = String(item.document_type || "").toLowerCase() === "rfi";

  let out;
  if (isRfi) {
    out = scopeStr ? `${scopeStr} - VQQ` : "VQQ";
  } else {
    out = scopeStr ? `Assessment - ${scopeStr}` : "Assessment";
  }
  return typeof out === "string" ? replaceRfiTerminologyForDisplay(out) : out;
}

/** Same string as the Document Name cell: RFI uses derived VQQ name; VQA uses `report_type`. */
function getLibraryRequestDocumentNameColumnSortString(item) {
  if (!item) return "";
  const isRfi = String(item.document_type || "").toLowerCase() === "rfi";
  const s = isRfi ? getLibraryRequestDocumentName(item) : (item?.report_type ?? "");
  return String(s).toLowerCase();
}

/** Location string for display; prefers structured city/state/country (same as My VQQs / My Assessments). */
export function getLibraryRequestLocationDisplay(item) {
  if (!item) return "";
  const fromStructured = getListRowLocationDisplay(item, {
    short: false,
    abbvState: false,
  });
  if (fromStructured) return fromStructured;

  const direct =
    item.location ??
    item.document_location ??
    item.vendor_location ??
    item.provider_location ??
    "";
  return String(direct).trim();
}

/** Raw date for Published column sorting. */
export function getLibraryRequestPublishedRaw(item) {
  if (!item) return null;
  const v =
    item.published_on ??
    item.publish_date ??
    item.publishDate ??
    item.document_published_on ??
    null;
  if (v == null || v === "") return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function getLibraryRequestPublishedDisplay(item) {
  const v =
    item?.published_on ??
    item?.publish_date ??
    item?.publishDate ??
    item?.document_published_on ??
    null;
  if (v == null || v === "") return "";
  return formatGridDate(v);
}

/** Raw cycle time from API; do not derive from request/release dates. */
export function getLibraryRequestCycleTimeFromApi(item) {
  if (!item) return "";
  const v = item.cycle_time ?? item.cycleTime;
  if (v == null || v === "") return "";
  return String(v).trim();
}

export function getLibraryRequestCycleTimeDisplay(item) {
  return getLibraryRequestCycleTimeFromApi(item);
}

/** Admin Library Requests column: same archive fields as other document grids. */
export function getLibraryRequestArchiveCommentDisplay(item) {
  return getGridArchiveCommentDisplay(item);
}

function naturalSortCompare(a, b) {
  if (!a && b) return -1;
  if (a && !b) return 1;
  if (!a && !b) return 0;
  const aStr = String(a).trim();
  const bStr = String(b).trim();
  const aParts = aStr.split(/(\d+)/).filter(Boolean);
  const bParts = bStr.split(/(\d+)/).filter(Boolean);
  const minLength = Math.min(aParts.length, bParts.length);
  for (let i = 0; i < minLength; i++) {
    if (!isNaN(aParts[i]) && !isNaN(bParts[i])) {
      const numA = parseInt(aParts[i], 10);
      const numB = parseInt(bParts[i], 10);
      if (numA !== numB) return numA - numB;
    } else if (aParts[i] !== bParts[i]) {
      return aParts[i].localeCompare(bParts[i]);
    }
  }
  return aParts.length - bParts.length;
}

/**
 * Client-side sort for library request rows (single active column).
 *
 * @param {object[]} items
 * @param {Record<string, { direction?: string }>} sortConfig
 */
export function sortLibraryRequestItems(items, sortConfig) {
  if (!items?.length) return [];
  const copy = [...items];
  if (Object.keys(sortConfig || {}).length === 0) return copy;

  const key = Object.keys(sortConfig)[0];
  const { direction } = sortConfig[key];

  return copy.sort((a, b) => {
    if (key === "document_name" || key === "report_type") {
      const valueA = getLibraryRequestDocumentNameColumnSortString(a);
      const valueB = getLibraryRequestDocumentNameColumnSortString(b);
      const result = naturalSortCompare(valueA, valueB);
      return direction === "asc" ? result : -result;
    }
    if (key === "provider_name") {
      const valueA = (a.provider_name || "").toLowerCase();
      const valueB = (b.provider_name || "").toLowerCase();
      const result = naturalSortCompare(valueA, valueB);
      return direction === "asc" ? result : -result;
    }
    if (key === "sponsor_name") {
      const valueA = (a.sponsor_name || "").toLowerCase();
      const valueB = (b.sponsor_name || "").toLowerCase();
      const result = naturalSortCompare(valueA, valueB);
      return direction === "asc" ? result : -result;
    }
    if (key === "location") {
      const valueA = getLibraryRequestLocationDisplay(a).toLowerCase();
      const valueB = getLibraryRequestLocationDisplay(b).toLowerCase();
      const result = naturalSortCompare(valueA, valueB);
      return direction === "asc" ? result : -result;
    }
    if (key === "request_status") {
      const aIndex = LIBRARY_REQUEST_STATUS_ORDER.indexOf(a[key] || "");
      const bIndex = LIBRARY_REQUEST_STATUS_ORDER.indexOf(b[key] || "");
      // Unknown statuses sort after known ones (stable relative order).
      const aRank =
        aIndex === -1 ? LIBRARY_REQUEST_STATUS_ORDER.length : aIndex;
      const bRank =
        bIndex === -1 ? LIBRARY_REQUEST_STATUS_ORDER.length : bIndex;
      if (aRank !== bRank) {
        return direction === "asc" ? aRank - bRank : bRank - aRank;
      }
      return 0;
    }
    if (key === "published_on") {
      const dateA = getLibraryRequestPublishedRaw(a);
      const dateB = getLibraryRequestPublishedRaw(b);
      if (!dateA && dateB) return direction === "asc" ? 1 : -1;
      if (dateA && !dateB) return direction === "asc" ? -1 : 1;
      if (!dateA && !dateB) return 0;
      return direction === "asc" ? dateA - dateB : dateB - dateA;
    }
    if (key === "requested_on" || key === "released_on") {
      const dateA = a[key] ? new Date(a[key]) : null;
      const dateB = b[key] ? new Date(b[key]) : null;
      if (!dateA && dateB) return direction === "asc" ? 1 : -1;
      if (dateA && !dateB) return direction === "asc" ? -1 : 1;
      if (!dateA && !dateB) return 0;
      return direction === "asc" ? dateA - dateB : dateB - dateA;
    }
    if (key === "cycle_time") {
      const valueA = getLibraryRequestCycleTimeFromApi(a);
      const valueB = getLibraryRequestCycleTimeFromApi(b);
      const isEmptyA = !valueA;
      const isEmptyB = !valueB;
      if (isEmptyA && !isEmptyB) return direction === "asc" ? 1 : -1;
      if (!isEmptyA && isEmptyB) return direction === "asc" ? -1 : 1;
      if (isEmptyA && isEmptyB) return 0;
      const result = naturalSortCompare(
        valueA.toLowerCase(),
        valueB.toLowerCase()
      );
      return direction === "asc" ? result : -result;
    }

    const valueA = a[key];
    const valueB = b[key];
    const isEmptyA = valueA === null || valueA === undefined || valueA === "";
    const isEmptyB = valueB === null || valueB === undefined || valueB === "";
    if (isEmptyA && !isEmptyB) return direction === "asc" ? 1 : -1;
    if (!isEmptyA && isEmptyB) return direction === "asc" ? -1 : 1;
    if (isEmptyA && isEmptyB) return 0;
    if (typeof valueA === "string" && typeof valueB === "string") {
      return direction === "asc"
        ? valueA.localeCompare(valueB)
        : valueB.localeCompare(valueA);
    }
    return direction === "asc"
      ? valueA > valueB
        ? 1
        : -1
      : valueA < valueB
      ? 1
      : -1;
  });
}

/**
 * `items` from `POST Documents/Requests` or admin `POST Documents/Admin/Requests`.
 * @param {'sponsor'|'provider'|'admin'} [audience] — provider grid must not read the sponsor envelope.
 */
export function getLibraryRequestItemsFromListPayload(requestList, audience = "sponsor") {
  if (!requestList || typeof requestList !== "object") return [];
  if (audience === "admin") {
    return Array.isArray(requestList.items) ? requestList.items : [];
  }
  if (audience === "provider") {
    return requestList.document_request_for_provider?.items || [];
  }
  return (
    requestList.document_request_for_sponsor?.items ||
    requestList.document_request_for_provider?.items ||
    []
  );
}

/**
 * Rows for Library Requests grids: when `useServerSort` is true, order is exactly
 * what the API returned (no client `sort`). Otherwise sorts the current page only.
 * @param {'sponsor'|'provider'|'admin'} [audience]
 */
export function getLibraryRequestsTableRows(
  requestList,
  sortConfig,
  useServerSort,
  audience = "sponsor"
) {
  const items = getLibraryRequestItemsFromListPayload(requestList, audience);
  if (!items.length) return [];
  if (useServerSort) return [...items];
  return sortLibraryRequestItems(items, sortConfig);
}

/** CSV / Excel: sponsor-facing grid (Vendor column). */
export const LIBRARY_REQUESTS_EXPORT_HEADERS = [
  "Document Name",
  "Vendor",
  "Scope",
  "Location",
  "Risk Summary",
  "Status",
  "Published",
  "Request Date",
  "Release Date",
  "Cycle Time",
];

/** CSV / Excel: vendor-facing grid (Sponsor column, no Risk Summary: vendors never see risk scoring). */
export const LIBRARY_REQUESTS_EXPORT_HEADERS_PROVIDER = [
  "Document Name",
  "Sponsor",
  "Scope",
  "Location",
  "Status",
  "Published",
  "Request Date",
  "Release Date",
  "Cycle Time",
];

/** CSV / Excel: admin grid (Sponsor + Vendor + Archive Comment). */
export const LIBRARY_REQUESTS_EXPORT_HEADERS_ADMIN = [
  "Document Name",
  "Sponsor",
  "Vendor",
  "Scope",
  "Location",
  "Risk Summary",
  "Status",
  "Published",
  "Request Date",
  "Release Date",
  "Cycle Time",
  "Archive Comment",
];

/**
 * @param {'sponsor'|'provider'|'admin'} [audience] — sponsor vs vendor vs admin
 */
export function getLibraryRequestsExportHeaders(audience = "sponsor") {
  if (audience === "admin") return [...LIBRARY_REQUESTS_EXPORT_HEADERS_ADMIN];
  return audience === "provider"
    ? [...LIBRARY_REQUESTS_EXPORT_HEADERS_PROVIDER]
    : [...LIBRARY_REQUESTS_EXPORT_HEADERS];
}

/**
 * @param {object} item — row from document_request_* list API
 * @param {{ audience?: 'sponsor'|'provider'|'admin' }} [options]
 */
export function mapLibraryRequestItemToExportRecord(item, options = {}) {
  const { audience = "sponsor", riskSummary = null } = options;
  const servicesFromCapsRaw = Array.isArray(item?.capabilities)
    ? item.capabilities
        .map((c) => c?.rfi_name || c?.services || "")
        .filter(Boolean)
        .join(", ")
    : "";
  const servicesFromCaps =
    typeof servicesFromCapsRaw === "string"
      ? replaceRfiTerminologyForDisplay(servicesFromCapsRaw)
      : servicesFromCapsRaw;
  const scopeExport = shouldShowAdditionalScopeOnLibraryItem(item)
    ? formatScopeWithAdditionalScopeForExport(item?.capabilities, item?.additional_scope)
    : servicesFromCaps;
  const base = {
    "Document Name": getLibraryRequestDocumentName(item),
    Scope: scopeExport,
    Location: getLibraryRequestLocationDisplay(item),
    // 14029 — Risk Summary cell mirrors the grid: empty when no published
    // snapshot applies (non-Diligent template, audit row, retracted, etc.).
    "Risk Summary": formatRiskSummaryForExport(riskSummary),
    Status: item?.request_status ?? "",
    Published: getLibraryRequestPublishedDisplay(item) || "",
    "Request Date": item?.requested_on
      ? formatGridDate(item.requested_on)
      : "",
    "Release Date": item?.released_on ? formatGridDate(item.released_on) : "",
    "Cycle Time": getLibraryRequestCycleTimeDisplay(item),
  };
  if (audience === "admin") {
    return {
      ...base,
      Sponsor: item?.sponsor_name ?? "",
      Vendor: item?.provider_name ?? "",
      "Archive Comment": getLibraryRequestArchiveCommentDisplay(item) || "",
    };
  }
  if (audience === "provider") {
    // Vendors never see risk scoring: no Risk Summary cell in their export.
    const { "Risk Summary": riskSummaryCell, ...withoutRisk } = base;
    return {
      ...withoutRisk,
      Sponsor: item?.sponsor_name ?? "",
    };
  }
  return {
    ...base,
    Vendor: item?.provider_name ?? "",
  };
}

/**
 * Sponsor and admin export of a Library Requests grid. Each VQQ row carries the
 * summary its grid cell shows, and a viewer without the Risk Summary column
 * (a sponsor whose org is not entitled) gets neither the column nor any score.
 * @param {unknown} items - the grid's rows, in grid order
 * @param {{ audience: 'sponsor'|'admin', summaries?: object, showRisk?: boolean }} options
 */
export function buildLibraryRequestsExportData(items, { audience, summaries = {}, showRisk = true }) {
  const rows = Array.isArray(items) ? items : [];
  const records = rows.map((item) =>
    mapLibraryRequestItemToExportRecord(item, {
      audience,
      riskSummary: showRisk ? getLibraryRequestRiskSummary(item, summaries) : null,
    })
  );
  const headers = getLibraryRequestsExportHeaders(audience);
  return { records, headers: showRisk ? headers : withoutRiskSummaryColumn(headers) };
}

/**
 * Vendor-facing export rows. Vendors never see risk scoring, so the rows are
 * built without fetching or attaching any Risk Summary.
 * @param {unknown} items - the grid's visible rows
 */
export function buildProviderVqaExportRecords(items) {
  const rows = Array.isArray(items) ? items : [];
  return rows.map((item) =>
    mapLibraryRequestItemToExportRecord(item, { audience: "provider" })
  );
}
